import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

export interface TenantUser {
  id: string;
  email: string;
  name: string | null;
  role: string;
  isActive: boolean;
  mfaRequired: boolean;
  restrictedModules: string[];
  lastLogin: string | null;
}

const ROLE_COLOR: Record<string, string> = {
  tenant_admin: 'var(--green)',
  manager:      'var(--amber)',
  viewer:       'var(--gray)',
};
const ROLE_BG: Record<string, string> = {
  tenant_admin: 'var(--green-bg)',
  manager:      'var(--amber-bg)',
  viewer:       '#f5f5f3',
};

export default function ClientUsersTab({ tenantId, tenantName }: { tenantId: string; tenantName: string }) {
  const { t } = useTranslation();
  const [users, setUsers]     = useState<TenantUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [form, setForm]       = useState({ email: '', password: '', name: '', role: 'viewer' });
  const [saving, setSaving]   = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/admin/tenants/${tenantId}/users`);
      setUsers(data);
    } catch {
      setError(t('users.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [tenantId]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.post(`/admin/tenants/${tenantId}/users`, form);
      setForm({ email: '', password: '', name: '', role: 'viewer' });
      load();
    } catch (err: any) {
      setError(err.response?.status === 409 ? t('tenants.usersModal.duplicateError') : (err.response?.data?.error ?? t('users.form.error')));
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (userId: string) => {
    if (!window.confirm(t('users.confirmDeactivate'))) return;
    await api.delete(`/admin/tenants/${tenantId}/users/${userId}`);
    load();
  };

  return (
    <div>
      <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>{t('tenants.usersModal.title', { tenant: tenantName })}</h2>

      {error && <div className="error-message" style={{ marginBottom: 14 }}>{error}</div>}

      {loading ? (
        <div style={{ color: 'var(--gray)', fontSize: 13, padding: '12px 0' }}>...</div>
      ) : users.length === 0 ? (
        <div style={{ color: 'var(--gray)', fontSize: 13, padding: '12px 0' }}>{t('tenants.usersModal.noUsers')}</div>
      ) : (
        <div style={{ marginBottom: 20, background: 'var(--white)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden' }}>
          {users.map((u, i) => (
            <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: i < users.length - 1 ? '1px solid var(--border)' : 'none' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name || u.email}</div>
                {u.name && <div style={{ fontSize: 11, color: 'var(--gray)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>}
              </div>
              <span style={{ fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 99, background: ROLE_BG[u.role] ?? '#f5f5f3', color: ROLE_COLOR[u.role] ?? 'var(--gray)', flexShrink: 0 }}>
                {t(`users.roles.${u.role}`, u.role)}
              </span>
              {u.isActive ? (
                u.role !== 'super_admin' && (
                  <button
                    onClick={() => handleDeactivate(u.id)}
                    style={{ fontSize: 11, padding: '3px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: 'var(--gray)', cursor: 'pointer', flexShrink: 0 }}
                  >
                    {t('users.deactivate')}
                  </button>
                )
              ) : (
                <span style={{ fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 99, background: '#f5f5f3', color: 'var(--gray)', flexShrink: 0 }}>
                  {t('status.inactive')}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>{t('tenants.usersModal.addNew')}</div>
      <form onSubmit={handleAdd} style={{ maxWidth: 420 }}>
        <div className="field">
          <label>{t('users.form.name')}</label>
          <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder={t('users.form.namePlaceholder')} />
        </div>
        <div className="field">
          <label>{t('users.form.email')}</label>
          <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required placeholder={t('users.form.emailPlaceholder')} />
        </div>
        <div className="field">
          <label>{t('users.form.tempPassword')}</label>
          <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required placeholder={t('users.form.passwordPlaceholder')} minLength={8} />
        </div>
        <div className="field">
          <label>{t('users.form.role')}</label>
          <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} style={{ width: '100%', padding: '10px 13px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, outline: 'none' }}>
            <option value="tenant_admin">{t('users.roles.tenant_admin')}</option>
            <option value="manager">{t('users.roles.manager')}</option>
            <option value="viewer">{t('users.roles.viewer')}</option>
          </select>
        </div>
        <button type="submit" disabled={saving} className="primary-button" style={{ marginTop: 8 }}>
          {saving ? t('users.form.saving') : t('users.form.create')}
        </button>
      </form>
    </div>
  );
}
